// validation.ts

export const BaseValidation: Record<string, RegExp> = {
  alphaNumeric: /^[a-zA-Z0-9]*$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
  url: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/,
  password: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{6,}$/,
  atleastOneAlphaOneNumeric: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/,
  alphabatic: /^[A-Za-z]+$/,
  nameAlphabatic: /^[A-Za-z .']+$/,
  numeric: /^[0-9]+$/,
  price: /^\d+(\.\d{1,2})?$/,
  username: /^[A-Za-z0-9 ]{2,20}$/,
  gst: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
  phone: /^[6-9]\d{9}$/,
  vehicleNumber: /^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/,
  pinCode: /^[1-9][0-9]{5}$/,
  hexColor: /^#?[A-Fa-f0-9]{6}$/,
  pfNumber: /^[A-Z]{2}\/[A-Z]{3}\/\d{7}\/\d{3}\/\d{7}$/,
  IFSC: /^[A-Z]{2}\/[A-Z]{3}\/\d{7}\/\d{3}\/\d{7}$/,
} as const;

// A generic type for a validation rule that has a value and a message
type Rule<T> = {
  value: T;
  message: string;
};

// Defines the structure for a set of validation rules for a single field
type FieldValidationRules = {
  pattern?: Rule<RegExp>;
  maxLength?: Rule<number>;
  minLength?: Rule<number>;
  validate?: (value: any) => boolean | string;
};
// Defines validation rules for phone objects (e.g., { phone: string })
type PhoneObjectValidationRules = {
  validate?: (value: { phone?: string }) => boolean | string;
};

// Defines the overall shape of the errorTypes object
type ErrorTypes = {
  required: (fieldName?: string) => {
    required: string;
    validate: (value: any) => boolean | string;
  };
  email: FieldValidationRules;
  phone: FieldValidationRules;
  phoneObject: PhoneObjectValidationRules;
  password: FieldValidationRules;
  name: FieldValidationRules;
  gstNumber: FieldValidationRules;
  pinCode: FieldValidationRules;
  aadhaar: FieldValidationRules;
  pan: FieldValidationRules;
  colorCode: FieldValidationRules;
  pfNumber: FieldValidationRules;
  IFSC: FieldValidationRules;
  dob: FieldValidationRules;
  joiningDate: FieldValidationRules;
  uan: FieldValidationRules;
  esic: FieldValidationRules;
  insurance: FieldValidationRules;
  drivingLicense: FieldValidationRules;
  city: FieldValidationRules;
  address: FieldValidationRules;
  accountNumber: FieldValidationRules;
  onlyAlphabets: FieldValidationRules;
  onlyNumbers: FieldValidationRules;
  alphanumeric: FieldValidationRules;
  description: FieldValidationRules;
  notFutureDate: (fieldName?: string) => {
    validate: (value: any) => boolean | string;
  };

  // Add other validation types here as needed
};

export const errorTypes: ErrorTypes = {
  required: (fieldName = "This field") => ({
    required: `${fieldName} is required`,
    validate: (value: any) => {
      if (typeof value === "string") {
        return value.trim().length > 0 || `${fieldName} is required`;
      }
      return value !== undefined && value !== null && value !== "";
    },
  }),

  email: {
    pattern: {
      value: BaseValidation.email,
      message: "Please enter a valid email address (e.g. name@example.com)",
    },
    validate: (value: any) => {
      if (!value) return true;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        if (!BaseValidation.email.test(trimmed)) {
          return "Please enter a valid email address (e.g. name@example.com)";
        }
      }
      return true;
    },
  },

  phone: {
    pattern: {
      value: BaseValidation.phone,
      message: "Please enter a valid 10-digit mobile number",
    },
    maxLength: {
      value: 10,
      message: "Mobile number cannot be more than 10 digits",
    },
    minLength: {
      value: 10,
      message: "Mobile number must be exactly 10 digits",
    },
  },

  phoneObject: {
    validate: (value) => {
      // If phone is empty, it's valid (optional field)
      if (!value?.phone || value.phone.trim() === "") {
        return true;
      }
      // If phone is provided, validate it
      if (!BaseValidation.phone.test(value.phone)) {
        return "Please enter a valid 10-digit mobile number";
      }
      if (value.phone.length !== 10) {
        return "Mobile number must be exactly 10 digits";
      }
      return true;
    },
  },

  password: {
    minLength: {
      value: 6,
      message: "Password must be at least 6 characters long",
    },
    // pattern: {
    //     value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,}$/,
    //     message:
    //         'Password must include uppercase, lowercase, number, and special character',
    // },
  },

  name: {
    pattern: {
      value: /^[a-zA-Z\s]*$/,
      message: "Name can only contain letters, spaces,",
    },
    validate: (value) => {
      if (!value) return true;
      const trimmed = value.trim();
      if (trimmed.length > 0 && !/^[A-Z]/.test(trimmed)) {
        return "First letter of Name must be uppercase ";
      }
      return true;
    },
    minLength: {
      value: 4,
      message: "Name must be at least 4 characters long",
    },
    maxLength: {
      value: 160,
      message: "Name should not exceed 160 characters",
    },
  },

  gstNumber: {
    pattern: {
      value: BaseValidation?.gst,
      message:
        "Please enter a valid 15-digit GST number (e.g., 22ABCDE1234F1Z5)",
    },
    maxLength: {
      value: 15,
      message: "GST number must be 15 characters",
    },
    minLength: {
      value: 15,
      message: "GST number must be 15 characters",
    },
  },

  pinCode: {
    pattern: {
      value: BaseValidation.pinCode,
      message: "Please enter a valid 6-digit PIN code",
    },
    maxLength: {
      value: 6,
      message: "PIN code must be exactly 6 digits",
    },
    minLength: {
      value: 6,
      message: "PIN code must be exactly 6 digits",
    },
  },

  aadhaar: {
    pattern: {
      value: /^[2-9]{1}[0-9]{11}$/, // Aadhaar must start from 2-9 and be 12 digits
      message:
        "Invalid Aadhaar Number (must be 12 digits, not starting with 0/1)",
    },
  },
  pan: {
    pattern: {
      value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, // PAN must follow AAAAA9999A format
      message: "Invalid PAN format (e.g., ABCDE1234F)",
    },
  },

  // IFSC: {
  //   pattern: {
  //     value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  //     message: "Invalid IFSC Code (e.g. SBIN0001234)",
  //   },
  //   maxLength: {
  //     value: 11,
  //     message: "IFSC code must be 11 characters",
  //   },
  //   minLength: {
  //     value: 0,
  //     message: "IFSC code must be more then 0 characters",
  //   },
  // },

  accountNumber: {
    pattern: {
      value: /^[0-9]{9,18}$/,
      message: "Please enter a valid Bank Account Number (9 to 18 digits)",
    },
    minLength: {
      value: 9,
      message: "Account number must be at least 9 digits",
    },
    maxLength: {
      value: 18,
      message: "Account number cannot exceed 18 digits",
    },
  },

  colorCode: {
    pattern: {
      value: BaseValidation.hexColor,
      message: "Please enter a valid 6-digit hex color (e.g. #A1B2C3)",
    },
    validate: (value) =>
      value.startsWith("#") || "Color code must start with #",
  },

  pfNumber: {
    validate: (value: string) => {
      if (!value) return true;
      const trimmed = value.trim();

      // 1. Old Format (with slashes e.g. TN/MAS/1234567/0001234 or MH/BAN/0012345/000/0001234)
      const oldFormatRegex =
        /^[A-Z]{2}\/[A-Z]{3}\/[0-9]{1,7}(\/[0-9]{1,3})?\/[0-9]{1,7}$/i;

      // 2. 22-character continuous alphanumeric format (e.g. TNMAS00123450000001234)
      const continuousFormatRegex = /^[A-Z]{2}[A-Z]{3}[0-9]{17}$/i;

      // 3. New Format (Numeric Member ID aligned with UAN, 7 to 12 digits)
      const numericMemberIdRegex = /^[0-9]{7,12}$/;

      if (
        oldFormatRegex.test(trimmed) ||
        continuousFormatRegex.test(trimmed) ||
        numericMemberIdRegex.test(trimmed)
      ) {
        return true;
      }

      return "Invalid PF format. Enter format like TN/MAS/1234567/0001234 or Numeric Member ID 7 to 12 Digit";
    },
  },

  IFSC: {
    pattern: {
      value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
      message:
        "Invalid IFSC format. Must be 4 letters, '0', and 6 letters/digits (e.g. SBIN0000691)",
    },
    minLength: {
      value: 11,
      message: "IFSC code must be exactly 11 characters",
    },
    maxLength: {
      value: 11,
      message: "IFSC code must be exactly 11 characters",
    },
  },

  uan: {
    pattern: {
      value: /^[0-9]{12}$/,
      message: "UAN must be exactly 12 numeric digits",
    },
    maxLength: {
      value: 12,
      message: "UAN must be exactly 12 digits",
    },
    minLength: {
      value: 12,
      message: "UAN must be exactly 12 digits",
    },
  },

  esic: {
    pattern: {
      value: /^[0-9]{10}$/,
      message: "ESIC number must be between 10 numeric digits",
    },
    maxLength: {
      value: 10,
      message: "ESIC number cannot exceed 10 digits",
    },
    minLength: {
      value: 10,
      message: "ESIC number must be at least 10 digits",
    },
  },

  insurance: {
    validate: (value: string) => {
      if (!value) return true;
      const trimmed = value.trim();

      // 1. Length check (10 to 25 characters)
      if (trimmed.length < 10 || trimmed.length > 25) {
        return "Insurance number must be between 10 and 25 characters";
      }

      // 2. Allowed characters: Alphanumeric, hyphen (-), slash (/), dot (.)
      if (!/^[A-Za-z0-9\/\-.]+$/.test(trimmed)) {
        return "Insurance number can only contain letters, numbers, hyphens (-) and slashes (/)";
      }

      // 3. Must contain at least one digit or letter
      if (!/[A-Za-z0-9]/.test(trimmed)) {
        return "Please enter a valid Insurance Policy Number";
      }

      return true;
    },
    maxLength: {
      value: 25,
      message: "Insurance number cannot exceed 25 characters",
    },
    minLength: {
      value: 10,
      message: "Insurance number must be at least 10 characters",
    },
  },

  drivingLicense: {
    validate: (value: string) => {
      if (!value) return true;

      const trimmed = value.trim();
      const raw = trimmed.replace(/[\s-]/g, "");

      // 1. Total length check (exactly 15 chars)
      if (raw.length !== 15) {
        return "Driving license must be exactly 15 characters (e.g. RJ1420180012345)";
      }

      // 2. First 2 characters: Strict UPPERCASE State Code
      const stateCode = raw.substring(0, 2);
      if (!/^[A-Z]{2}$/.test(stateCode)) {
        return "First 2 characters must be uppercase state code (e.g. RJ, MH, DL)";
      }

      // 3. Next 2 characters: RTO Code (2 digits)
      const rtoCode = raw.substring(2, 4);
      if (!/^[0-9]{2}$/.test(rtoCode)) {
        return "Characters 3 & 4 must be a 2-digit RTO code (e.g. 14, 01)";
      }

      // 4. Next 4 characters: Valid Year of Issue
      const yearStr = raw.substring(4, 8);
      if (!/^[0-9]{4}$/.test(yearStr)) {
        return "Year of issue (digits 5-8) must be 4 numbers";
      }

      const year = parseInt(yearStr, 10);
      const currentYear = new Date().getFullYear();
      if (year < 1960 || year > currentYear) {
        return `Year of issue must be between 1960 and ${currentYear}`;
      }

      // 5. Last 7 characters: Unique license digits
      const licenseNumber = raw.substring(8, 15);
      if (!/^[0-9]{7}$/.test(licenseNumber)) {
        return "Last 7 characters must be numeric license number";
      }

      return true;
    },
  },

  dob: {
    validate: (value: string) => {
      if (!value) return true; // Let required rule handle empty values

      const birthDate = new Date(value);
      if (isNaN(birthDate.getTime())) {
        return "Please enter a valid date";
      }

      const today = new Date();

      // 1. Future date check
      if (birthDate > today) {
        return "Date of birth cannot be in the future";
      }

      // 2. Exact Age Calculation
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      // 3. Minimum 18 years check
      if (age < 18) {
        return "Employee must be at least 18 years old";
      }

      // 4. Maximum realistic age limit
      if (age > 100) {
        return "Please enter a valid Date of Birth";
      }

      return true;
    },
  },

  notFutureDate: (fieldName = "Date") => ({
    validate: (value: string) => {
      if (!value) return true;
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        return `${fieldName} cannot be in the future`;
      }
      return true;
    },
  }),

  joiningDate: {
    validate: (value: string) => {
      if (!value) return true;
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        return "Joining Date cannot be in the future";
      }
      return true;
    },
  },

  city: {
    pattern: {
      value: /^[A-Z][a-zA-Z\s]*$/,
      message:
        "First letter must be uppercase and only letters are allowed (no numbers or special characters)",
    },
    minLength: {
      value: 4,
      message: "City must be at least 4 characters",
    },
    maxLength: {
      value: 50,
      message: "City cannot exceed 50 characters",
    },
  },

  address: {
    validate: (value: string) => {
      if (!value) return true;

      const trimmed = value.trim();

      // Minimum length
      if (trimmed.length < 10) {
        return "Address must be at least 10 characters long";
      }

      // Maximum length is handled below

      // Must contain at least one alphabet
      if (!/[\p{L}]/u.test(trimmed)) {
        return "Address must contain letters";
      }

      // Reject special characters that are not normally useful in an address
      if (!/^[\p{L}\p{N}\s,./#()'&-]+$/u.test(trimmed)) {
        return `Address contains invalid characters, please use only letters, numbers, spaces, and these symbols: , . / # ( ) ' -`;
      }

      return true;
    },

    maxLength: {
      value: 150,
      message: "Address cannot exceed 150 characters",
    },
  },

  onlyAlphabets: {
    pattern: {
      value: /^[A-Za-z\s]+$/,
      message: "Only alphabets are allowed",
    },
    minLength: {
      value: 4,
      message: " must be at least 4 characters",
    },
    maxLength: {
      value: 50,
      message: " cannot exceed 50 characters",
    },
  },

  onlyNumbers: {
    pattern: {
      value: /^[0-9]+$/,
      message: "Only numbers are allowed",
    },
    minLength: {
      value: 2,
      message: " must be at least 2 digits",
    },
    maxLength: {
      value: 50,
      message: " cannot exceed 50 digits",
    },
  },

  alphanumeric: {
    pattern: {
      value: /^[A-Za-z0-9\s]+$/,
      message: "Only letters and numbers are allowed",
    },
    validate: (value: unknown) => {
      if (typeof value !== "string" || !value) return true;
      const trimmed = value.trim();
      if (trimmed.length > 0 && !/[A-Za-z]/.test(trimmed)) {
        return "Must contain at least one letter";
      }
      return true;
    },
    minLength: {
      value: 4,
      message: " must be at least 4 characters",
    },
    maxLength: {
      value: 15,
      message: " cannot exceed 15 characters",
    },
  },

  description: {
    validate: (value: unknown) => {
      if (typeof value !== "string" || !value) return true;
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        if (trimmed.length < 10) {
          return "Description must be at least 10 characters long";
        }
        if (!/[A-Za-z!.,?\s]/.test(trimmed)) {
          return "Description must contain letters or special characters";
        }
        if (!/^[A-Z]/.test(trimmed)) {
          return "First letter of description must be uppercase";
        }
      }
      return true;
    },
    maxLength: {
      value: 500,
      message: "Description should not exceed 500 characters",
    },
  },
};
